import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Box from "@mui/material/Box";
import type {ColumnDef, Row} from "@tanstack/react-table";
import type {
    DailySpaV3RequestDTO,
    DailySpaV3ReservationDTO,
    DailySpaV3RowDTO,
} from "crqlar-react-common/dist/models/aggregation/dailyAggregation.model";
import type {ResourceDisplayReference} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import {
    DataFilterOperator,
    type Filter,
    type FilterRule,
} from "crqlar-react-common/dist/models/audience/filter.model";
import type {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {DailyListAggregationService} from "crqlar-react-common/dist/services/aggregation/dailyListAggregation.service";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {formatDateHM} from "crqlar-react-common/dist/utils/date.util";
import {enqueueSnackbar} from "notistack";
import {type ReactNode, useCallback, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    RightSidebarDivider,
    RightSidebarFullscreenExitButton,
    RightSidebarPanelButton,
    RightSidebarRail,
    RightSidebarRailHeader,
    RightSidebarRailHeaderCenter,
    RightSidebarRailLayout,
} from "../layout/rightSidebar/RightSidebarRail";
import {
    RadioFilterOption,
    RadioFilterPanel,
} from "../layout/rightSidebar/RadioFilterPanel";
import {
    StayStatusFilterValue,
    StayStatusRailFilter,
} from "../layout/rightSidebar/StayStatusRailFilter";
import SingleDatePicker from "../common/newDatePicker/SingleDatePicker";
import {
    type DatePickerChangeReason,
    useDebouncedSingleDate,
} from "../common/newDatePicker/datePickerShared";
import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    ServerTableRailColumns,
    ServerTableRailExport,
    ServerTableRailFullscreen,
    ServerTableRailPrint,
    ServerTableRailSearch,
    ServerTableRailSort,
    serverTableExportLimit,
    type ServerTableExportQuery,
    useServerTable,
    useServerTableContext,
} from "../common/serverTable";
import {formatProfileListName} from "crqlar-react-common/dist/utils/profile.util";
import {AllergiesElement} from "../common/uiElements/AllergiesElement";
import {BirthdayElement} from "../common/uiElements/BirthdayElement";
import {CountryElement} from "../common/uiElements/CountryElement";
import {DateElement} from "../common/uiElements/DateElement";
import {DietsElement} from "../common/uiElements/DietsElement";
import {GenderElement} from "../common/uiElements/GenderElement";
import {LanguagesElement} from "../common/uiElements/LanguagesElement";
import {MedicalElement} from "../common/uiElements/MedicalElement";
import {NationalityElement} from "../common/uiElements/NationalityElement";
import {NotesElement} from "../common/uiElements/NotesElement";
import {noteTexts} from "crqlar-react-common/dist/utils/noteTypeGroup.util";
import {RecommendationsElement} from "../common/uiElements/RecommendationsElement";
import {
    ReservationsElement,
    type ReservationElementItem,
} from "../common/uiElements/ReservationsElement";
import {
    AccommodationReferences,
    FacilityElement,
    RoomStatusElement,
    StaffElement,
} from "../common/uiElements/ResourceElement";
import {SimpleChipElement} from "../common/uiElements/SimpleChipElement";
import {StayRangeElement} from "../common/uiElements/StayRangeElement";
import {StayStatusElement} from "../common/uiElements/StayStatusElement";
import {getLocalizedValue} from "../common/uiElements/utils";
import DigitalSigningDetailsDialog from "../digitalSigning/details/DigitalSigningDetailsDialog";
import {createDigitalSigningColumn} from "../digitalSigning/list/digitalSigningColumn";
import {ReservationResourcePicker} from "../reservations/rail/ReservationResourcePicker";
import {useReservationResourceSelection} from "../reservations/rail/useReservationResourceSelection";
import ReservationDialog from "../reservations/reservationDialog/ReservationDialog";
import {createReservationUpdatedColumn} from "../reservations/lists/reservationUpdatedColumn";
import {ageFromBirthday} from "./dailyListAge";
import {applyDailyListColumnDefaults} from "./dailyListDefaults";
import {SpaDailyListFilterButton} from "./filters/DailyListFilterButtons";

const getSubRows = (row: DailySpaV3RowDTO) => row.children;

const localizedInterestValues = (row: DailySpaV3RowDTO, locale: string) => (
    (row.interests ?? [])
        .map(interest => getLocalizedValue(interest.localizations, locale) ?? interest.value)
        .filter((value): value is string => Boolean(value))
);

const reservationResources = (
    row: DailySpaV3RowDTO,
    select: (reservation: DailySpaV3ReservationDTO) => ResourceDisplayReference[] | undefined,
) => Array.from(new Map(
    row.spaReservations.flatMap(reservation => select(reservation) ?? []).map(resource => [resource.id, resource]),
).values());

const spaResourceNames = (reservation: DailySpaV3ReservationDTO) => (
    [...(reservation.staffResources ?? []), ...(reservation.facilityResources ?? [])]
        .map(resource => resource.name)
        .filter((name): name is string => Boolean(name))
);

const spaReservationItems = (row: DailySpaV3RowDTO): ReservationElementItem[] => (
    row.spaReservations.map(reservation => ({
        reservationId: reservation.reservationId,
        status: reservation.status,
        start: reservation.start,
        end: reservation.end,
        serviceName: reservation.serviceName,
        resources: [...(reservation.staffResources ?? []), ...(reservation.facilityResources ?? [])],
        guestCount: reservation.guestCount,
    }))
);

type DailyListViewMode = "GROUPED" | "INDIVIDUAL";

interface SpaDailyListTableProps {
    children: ReactNode;
    selectedDate: Date;
    onDateChange: (date: Date, reason: DatePickerChangeReason) => void;
}

const SpaDailyListTable = ({children, selectedDate, onDateChange}: SpaDailyListTableProps) => {
    const {isFullScreen, setIsFullScreen} = useServerTableContext();

    return (
        <RightSidebarRailLayout>
            <RightSidebarRailHeader compact={isFullScreen}>
                <RightSidebarFullscreenExitButton
                    active={isFullScreen}
                    onExit={() => setIsFullScreen(false)}
                />

                <RightSidebarRailHeaderCenter>
                    <SingleDatePicker
                        value={selectedDate}
                        onChange={onDateChange}
                        size="medium"
                    />
                </RightSidebarRailHeaderCenter>
            </RightSidebarRailHeader>

            <ServerTable/>

            <RightSidebarRail>
                {children}
            </RightSidebarRail>
        </RightSidebarRailLayout>
    );
};

export default function SpaDailyList({sectorId}: { sectorId: string }) {
    const {t, i18n} = useTranslation();
    const {date: selectedDate, queryDate, onDateChange} = useDebouncedSingleDate(sectorId);
    const [viewMode, setViewMode] = useState<DailyListViewMode>("GROUPED");
    const [stayStatus, setStayStatus] = useState<StayStatusFilterValue>("ALL");
    const [filterRules, setFilterRules] = useState<FilterRule[]>([]);
    const [selectedReservation, setSelectedReservation] = useState<Reservation>();
    const [digitalSigningId, setDigitalSigningId] = useState<string>();
    const grouped = viewMode === "GROUPED";
    const locale = i18n.resolvedLanguage || i18n.language;
    const filters = useMemo<Filter | undefined>(() => filterRules.length ? {
        filterGroups: [{filterRules, operator: DataFilterOperator.AND}],
    } : undefined, [filterRules]);
    const resourceSelection = useReservationResourceSelection({sectorId, viewKey: "daily"});
    // Everything selected is the same as no filter, so send nothing and let the backend skip it.
    const selectedResourceIds = resourceSelection.resources.length === 0 || resourceSelection.allSelected
        ? undefined
        : resourceSelection.selectedIds;
    // Deselecting every resource means "show nothing" — an empty list would otherwise read as "no filter".
    const noResourcesSelected = resourceSelection.resources.length > 0
        && resourceSelection.selectedIds.length === 0;

    const openReservationById = useCallback(async (reservationId: string | undefined) => {
        if (!reservationId) return;

        try {
            const fullReservation = await ReservationService.getReservationById(reservationId);
            if (!fullReservation) throw new Error("Reservation not found");
            setSelectedReservation(fullReservation);
        } catch (error) {
            console.error(error);
            enqueueSnackbar(t("Failed to open reservation"), {variant: "error"});
        }
    }, [t]);

    const handleOpenReservation = useCallback(
        (reservation: ReservationElementItem) => openReservationById(reservation.reservationId),
        [openReservationById],
    );

    const getRowId = useCallback((
        row: DailySpaV3RowDTO,
        _index: number,
        parent?: Row<DailySpaV3RowDTO>,
    ) => parent ? `${parent.id}/${row.rowId}` : row.rowId, []);

    const viewModeOptions = useMemo<readonly RadioFilterOption<DailyListViewMode>[]>(() => [
        {value: "GROUPED", label: t("Grouped")},
        {value: "INDIVIDUAL", label: t("Individual guests")},
    ], [t]);

    const columns = useMemo<ColumnDef<DailySpaV3RowDTO>[]>(() => applyDailyListColumnDefaults("spa", [
        {
            id: "name",
            header: t("Name"),
            accessorFn: row => formatProfileListName(row),
            cell: ({getValue, row}) => (
                <Box sx={{fontWeight: row.getCanExpand() ? 600 : undefined}}>
                    {getValue<string>()}
                </Box>
            ),
            size: 240,
            meta: {
                isServerSortable: true,
                serverSortField: "lastName",
                printable: true,
                printDefault: true,
            },
        },
        {
            id: "lastNameDetails",
            header: t("Last name"),
            accessorFn: row => row.lastName,
            cell: ({getValue, row}) => (
                <Box sx={{fontWeight: row.getCanExpand() ? 600 : undefined}}>
                    {getValue<string | undefined>() ?? null}
                </Box>
            ),
            size: 160,
            meta: {
                isServerSortable: true,
                serverSortField: "lastName",
                printable: true,
            },
        },
        {
            id: "firstNameDetails",
            header: t("First name"),
            accessorFn: row => row.firstName,
            cell: ({getValue}) => <Box>{getValue<string | undefined>() ?? null}</Box>,
            size: 150,
            meta: {
                isServerSortable: true,
                serverSortField: "firstName",
                printable: true,
            },
        },
        {
            id: "age",
            header: t("Age"),
            accessorFn: row => ageFromBirthday(row.birthday),
            enableSorting: false,
            cell: ({getValue}) => <Box>{getValue<number | undefined>() ?? null}</Box>,
            size: 90,
            meta: {
                printable: true,
                printDefault: true,
                printCell: row => row.getValue<number | undefined>("age") ?? "",
            },
        },
        {
            id: "spaReservations",
            header: t("Reservations"),
            accessorFn: row => row.spaReservations.map(reservation => {
                const time = reservation.start ? formatDateHM(reservation.start) : undefined;
                const label = reservation.serviceName?.trim()
                    || spaResourceNames(reservation).join(", ");
                const guestCount = reservation.guestCount === undefined
                    ? undefined
                    : t("Guest count short", {count: reservation.guestCount});

                return [time, label, guestCount].filter(Boolean).join(" ");
            }).join("; "),
            cell: ({row}) => row.depth === 0 ? (
                <ReservationsElement
                    reservations={spaReservationItems(row.original)}
                    now={new Date()}
                    dateContext={queryDate}
                    onSelect={handleOpenReservation}
                />
            ) : null,
            size: 360,
            meta: {
                isServerSortable: true,
                grow: true,
                printable: true,
                printDefault: true,
                // A group's guest rows share its booking: it prints once, on the group row, as on screen.
                printCell: row => row.depth === 0 ? row.getValue<string>("spaReservations") : "",
                printOriginalCell: row => row.depth === 0 ? (
                    <ReservationsElement
                        reservations={spaReservationItems(row.original)}
                        now={new Date()}
                        asPrint
                        printVariant="original"
                        dateContext={queryDate}
                    />
                ) : null,
            },
        },
        {
            id: "guestCount",
            header: t("Guest count abbreviation"),
            accessorFn: row => row.guestCount,
            cell: ({getValue}) => <Box>{getValue<number | undefined>() ?? null}</Box>,
            size: 120,
            meta: {
                isServerSortable: true,
                printable: true,
                // The booking's guest count, printed once on the group row rather than on every guest of it.
                printCell: row => row.depth === 0 ? (row.getValue<number | undefined>("guestCount") ?? "") : "",
            },
        },
        {
            id: "rooms",
            header: t("Room"),
            accessorFn: row => row.accommodationResources?.map(resource => resource.name).filter(Boolean) ?? [],
            cell: ({row}) => <AccommodationReferences resources={row.original.accommodationResources}/>,
            size: 100,
            meta: {
                isServerSortable: true,
                serverSortField: "accommodationResources",
                printable: true,
                printDefault: true,
                printCell: row => row.original.accommodationResources
                    ?.map(resource => resource.name).filter(Boolean).join(", ") ?? null,
            },
        },
        {
            id: "roomStatus",
            header: t("Room status"),
            accessorFn: row => row.accommodationResources?.map(resource => resource.name).filter(Boolean) ?? [],
            cell: ({row}) => (
                <RoomStatusElement
                    resources={row.original.accommodationResources}
                    stayStatus={row.original.stayStatus}
                    onClick={row.original.reservationId
                        ? () => void openReservationById(row.original.reservationId)
                        : undefined}
                />
            ),
            size: 180,
            meta: {
                printable: true,
                printCell: row => (
                    <RoomStatusElement
                        resources={row.original.accommodationResources}
                        stayStatus={row.original.stayStatus}
                        asPrint
                    />
                ),
            },
        },
        {
            id: "countryCode",
            header: t("Country"),
            accessorFn: row => row.countryCode,
            cell: ({row}) => <CountryElement countryCode={row.original.countryCode}/>,
            size: 130,
            meta: {
                printable: true,
                printDefault: true,
                printCell: row => <CountryElement countryCode={row.original.countryCode} asPrint/>,
            },
        },
        {
            id: "nationality",
            header: t("Nationality"),
            accessorFn: row => row.nationality,
            cell: ({row}) => <NationalityElement nationality={row.original.nationality}/>,
            size: 130,
            meta: {
                printable: true,
                printDefault: true,
                printCell: row => <NationalityElement nationality={row.original.nationality} asPrint/>,
            },
        },
        {
            id: "languageCodes",
            header: t("Languages"),
            accessorFn: row => row.languageCodes ?? [],
            cell: ({row}) => <LanguagesElement languageCodes={row.original.languageCodes}/>,
            size: 150,
            meta: {
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => <LanguagesElement languageCodes={row.original.languageCodes} asPrint/>,
            },
        },
        {
            id: "stayRange",
            header: t("Stay range"),
            accessorFn: row => row.stayStart,
            cell: ({row}) => (
                <StayRangeElement
                    start={row.original.stayStart}
                    end={row.original.stayEnd}
                    locale={locale}
                />
            ),
            size: 180,
            meta: {
                isServerSortable: true,
                serverSortField: "stayStart",
                printable: true,
                printDefault: true,
                printCell: row => (
                    <StayRangeElement
                        start={row.original.stayStart}
                        end={row.original.stayEnd}
                        locale={locale}
                    />
                ),
            },
        },
        {
            id: "interests",
            header: t("Interests"),
            accessorFn: row => localizedInterestValues(row, locale),
            cell: ({row}) => (
                <SimpleChipElement values={localizedInterestValues(row.original, locale)}/>
            ),
            size: 260,
            meta: {
                printable: true,
                printDefault: true,
                printCell: row => (
                    <SimpleChipElement values={localizedInterestValues(row.original, locale)} asPrint/>
                ),
            },
        },
        {
            id: "allergies",
            header: t("Allergies"),
            accessorFn: row => row.allergies ?? [],
            cell: ({row}) => <AllergiesElement allergies={row.original.allergies}/>,
            size: 220,
            meta: {printable: true},
        },
        {
            id: "diets",
            header: t("Diets"),
            accessorFn: row => row.diets ?? [],
            cell: ({row}) => <DietsElement diets={row.original.diets}/>,
            size: 220,
            meta: {printable: true},
        },
        {
            id: "medical",
            header: t("Medical"),
            accessorFn: row => row.medical ?? [],
            cell: ({row}) => <MedicalElement medical={row.original.medical}/>,
            size: 220,
            meta: {printable: true, printDefault: true},
        },
        {
            id: "recommendations",
            header: t("Recommendations"),
            accessorFn: row => [
                ...(row.warningRecommendation ?? []),
                ...(row.informationRecommendation ?? []),
                ...(row.successRecommendation ?? []),
            ],
            cell: ({row}) => (
                <RecommendationsElement
                    warningMessages={row.original.warningRecommendation}
                    informationMessages={row.original.informationRecommendation}
                    successMessages={row.original.successRecommendation}
                    locale={locale}
                />
            ),
            size: 360,
            meta: {
                printable: true,
                printDefault: true,
                printCell: row => (
                    <RecommendationsElement
                        warningMessages={row.original.warningRecommendation}
                        informationMessages={row.original.informationRecommendation}
                        successMessages={row.original.successRecommendation}
                        locale={locale}
                        printMode
                    />
                ),
            },
        },
        {
            id: "staff",
            header: t("Staff"),
            accessorFn: row => reservationResources(row, reservation => reservation.staffResources)
                .map(resource => resource.name).filter(Boolean),
            cell: ({row}) => {
                const resources = reservationResources(row.original, reservation => reservation.staffResources);
                if (!resources.length) return null;

                return (
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                        {resources.map(resource => (
                            <StaffElement key={resource.id} name={resource.name ?? ""} color={resource.tagColor}/>
                        ))}
                    </Box>
                );
            },
            size: 220,
            meta: {
                isServerSortable: true,
                serverSortField: "staffResources",
                printable: true,
                printCell: row => reservationResources(
                    row.original,
                    reservation => reservation.staffResources,
                ).map(resource => resource.name).filter(Boolean).join(", "),
            },
        },
        {
            id: "facilities",
            header: t("Facility"),
            accessorFn: row => reservationResources(row, reservation => reservation.facilityResources)
                .map(resource => resource.name).filter(Boolean),
            cell: ({row}) => {
                const resources = reservationResources(row.original, reservation => reservation.facilityResources);
                if (!resources.length) return null;

                return (
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                        {resources.map(resource => (
                            <FacilityElement key={resource.id} name={resource.name ?? ""} color={resource.tagColor}/>
                        ))}
                    </Box>
                );
            },
            size: 220,
            meta: {
                isServerSortable: true,
                serverSortField: "facilityResources",
                printable: true,
                printCell: row => reservationResources(
                    row.original,
                    reservation => reservation.facilityResources,
                ).map(resource => resource.name).filter(Boolean).join(", "),
            },
        },
        {
            id: "birthday",
            header: t("Birthday"),
            accessorFn: row => row.birthday,
            cell: ({row}) => (
                <BirthdayElement
                    birthday={row.original.birthday}
                    selectedDate={queryDate}
                    name={formatProfileListName(row.original)}
                />
            ),
            size: 170,
            meta: {
                isServerSortable: true,
                serverSortField: "birthday",
                printable: true,
                printCell: row => (
                    <BirthdayElement
                        birthday={row.original.birthday}
                        selectedDate={queryDate}
                        name={formatProfileListName(row.original)}
                    />
                ),
            },
        },
        {
            id: "stayStatus",
            header: t("Stay status"),
            accessorFn: row => row.stayStatus,
            cell: ({row}) => (
                <StayStatusElement
                    stayStatus={row.original.stayStatus}
                    startDate={row.original.stayStart}
                    endDate={row.original.stayEnd}
                />
            ),
            size: 170,
            meta: {
                isServerSortable: true,
                printable: true,
                printCell: row => <StayStatusElement stayStatus={row.original.stayStatus} asPrint/>,
            },
        },
        {
            id: "start",
            header: t("Start"),
            accessorFn: row => row.start,
            cell: ({row}) => <DateElement date={row.original.start}/>,
            size: 160,
            meta: {
                isServerSortable: true,
                printable: true,
                printCell: row => <DateElement date={row.original.start}/>,
            },
        },
        {
            id: "end",
            header: t("End"),
            accessorFn: row => row.end,
            cell: ({row}) => <DateElement date={row.original.end}/>,
            size: 160,
            meta: {
                isServerSortable: true,
                printable: true,
                printCell: row => <DateElement date={row.original.end}/>,
            },
        },
        // A row is one reservation; the API leaves a group's guest rows, which repeat their parent's, without
        createDigitalSigningColumn<DailySpaV3RowDTO>(
            t("Signature"),
            row => row.spaReservations[0]?.digitalSigning,
            setDigitalSigningId,
        ),
        {
            id: "gender",
            header: t("Gender"),
            accessorFn: row => row.gender,
            cell: ({row}) => <GenderElement gender={row.original.gender}/>,
            size: 140,
            meta: {
                printable: true,
                printCell: row => <GenderElement gender={row.original.gender}/>,
            },
        },
        {
            id: "voiceNotes",
            header: t("Voice Notes"),
            accessorFn: row => noteTexts(row.voiceNotes),
            cell: ({row}) => <NotesElement notes={row.original.voiceNotes}/>,
            size: 280,
            meta: {
                printable: true,
                printCell: row => <NotesElement notes={row.original.voiceNotes} asPrint/>,
            },
        },
        {
            id: "email",
            header: t("Email"),
            accessorFn: row => row.primaryEmail,
            cell: ({getValue}) => <Box>{getValue<string | undefined>() ?? null}</Box>,
            size: 260,
            meta: {isServerSortable: true, serverSortField: "primaryEmail", printable: true},
        },
        {
            id: "namePrefix",
            header: t("Name prefix"),
            accessorFn: row => row.namePrefix,
            cell: ({getValue}) => <Box>{getValue<string | undefined>() ?? null}</Box>,
            size: 140,
            meta: {printable: true},
        },
        {
            id: "academicTitle",
            header: t("Academic title"),
            accessorFn: row => row.academicTitle,
            cell: ({getValue}) => <Box>{getValue<string | undefined>() ?? null}</Box>,
            size: 150,
            meta: {printable: true},
        },
        {
            id: "id",
            header: t("ID"),
            accessorFn: row => row.profileId,
            cell: ({getValue}) => <Box>{getValue<string | undefined>() ?? null}</Box>,
            size: 260,
            meta: {isServerSortable: true, serverSortField: "profileId"},
        },
        createReservationUpdatedColumn<DailySpaV3RowDTO>(t("Reservation updated")),
    ]), [handleOpenReservation, locale, openReservationById, queryDate, t]);

    const controller = useServerTable<DailySpaV3RowDTO>({
        tableId: "spa-daily-list-v3",
        columns,
        getRowId,
        getSubRows: grouped ? getSubRows : undefined,
        // Print covers the whole filtered day, not only the loaded page (CV3-1876).
        allRows: {},
        initialSorting: [{id: "spaReservations", desc: false}],
        queryDependencies: [
            sectorId,
            queryDate.getTime(),
            viewMode,
            stayStatus,
            resourceSelection.resources.length,
            resourceSelection.allSelected,
            resourceSelection.selectedIds.join(","),
            filters,
        ],
        queryFn: async ({pagination, searchQuery, sorting}) => {
            if (noResourcesSelected) {
                return {content: [], totalElements: 0};
            }

            const page = await DailyListAggregationService.listSpa({
                sectorId,
                date: queryDate,
                grouped,
                resourceIds: selectedResourceIds,
                stayStatuses: stayStatus === "ALL" ? undefined : [stayStatus],
                filters,
                query: searchQuery || undefined,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined,
                },
            });

            return {
                content: page.content,
                totalElements: page.totalElements,
            };
        },
    });

    // The export's request: the list's own, without paging; the API writes one row per guest (CV3-1876).
    const exportRequest = useCallback((
        {searchQuery, sorting}: ServerTableExportQuery,
    ): DailySpaV3RequestDTO | undefined => noResourcesSelected ? undefined : {
        sectorId,
        date: queryDate,
        grouped,
        resourceIds: selectedResourceIds,
        stayStatuses: stayStatus === "ALL" ? undefined : [stayStatus],
        filters,
        query: searchQuery || undefined,
        limit: serverTableExportLimit(sorting),
    }, [filters, grouped, noResourcesSelected, queryDate, sectorId, selectedResourceIds, stayStatus]);

    return (
        <>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <SpaDailyListTable
                        selectedDate={selectedDate}
                        onDateChange={onDateChange}
                    >
                        <ServerTableRailSearch/>
                        <ServerTableRailSort/>
                        <StayStatusRailFilter
                            value={stayStatus}
                            onChange={setStayStatus}
                        />
                        <SpaDailyListFilterButton
                            sectorId={sectorId}
                            resources={resourceSelection.resources}
                            rules={filterRules}
                            onChange={setFilterRules}
                        />
                        <RightSidebarPanelButton
                                icon={<VisibilityOutlinedIcon/>}
                                label={t("View mode")}
                                renderPanel={close => (
                                    <RadioFilterPanel<DailyListViewMode>
                                        title={t("View mode")}
                                        options={viewModeOptions}
                                        value={viewMode}
                                        onChange={setViewMode}
                                        onReset={grouped ? undefined : () => setViewMode("GROUPED")}
                                        onClose={close}
                                    />
                                )}
                        />
                        <ServerTableRailFullscreen/>
                        <ServerTableRailColumns/>
                        <RightSidebarDivider/>
                        <ReservationResourcePicker
                            selection={resourceSelection}
                            focusedDate={selectedDate}
                        />
                        <ServerTableRailPrint
                            title={t("Daily List")}
                            selectedDate={queryDate}
                        />
                        <ServerTableRailExport
                            list="daily-spa"
                            title={t("Daily List")}
                            buildRequest={exportRequest}
                        />
                    </SpaDailyListTable>
                </ServerTableFrame>
            </ServerTableProvider>

            <ReservationDialog
                open={Boolean(selectedReservation)}
                reservation={selectedReservation}
                onClose={() => setSelectedReservation(undefined)}
                onSave={() => {
                    setSelectedReservation(undefined);
                    void controller.query.refetch();
                }}
                onChangeReservation={setSelectedReservation}
            />

            <DigitalSigningDetailsDialog
                id={digitalSigningId}
                open={Boolean(digitalSigningId)}
                onClose={() => setDigitalSigningId(undefined)}
            />
        </>
    );
}
